import { useEffect, useRef, useState } from 'react'
import { CheckCircle2 } from 'lucide-react'
import { publicApi } from '@/lib/api'

type DsrType =
  | 'ACCESS'
  | 'RECTIFICATION'
  | 'ERASURE'
  | 'OBJECTION'
  | 'PORTABILITY'
  | 'RESTRICTION'
  | 'COMPLAINT'

const TYPE_OPTIONS: { value: DsrType; label: string }[] = [
  { value: 'ACCESS', label: 'Acceso (art. 15) — copia de los datos que tenemos sobre ti' },
  { value: 'RECTIFICATION', label: 'Rectificación (art. 16) — corregir datos inexactos' },
  { value: 'ERASURE', label: 'Supresión / derecho al olvido (art. 17)' },
  { value: 'OBJECTION', label: 'Oposición (art. 21) — dejar de tratar tus datos' },
  { value: 'PORTABILITY', label: 'Portabilidad (art. 20) — recibir tus datos en formato reutilizable' },
  { value: 'RESTRICTION', label: 'Limitación del tratamiento (art. 18)' },
  { value: 'COMPLAINT', label: 'Queja / otra consulta sobre tus datos' },
]

interface RetentionDisclosure {
  what: string
  basis: string
}

interface ApiResponse {
  id: string
  message: string
  deadlineAt: string
  legalNote?: string
  retentionDisclosures?: RetentionDisclosure[]
}

const ERASURE_PREVIEW: RetentionDisclosure[] = [
  {
    what: 'Facturación / contabilidad',
    basis: 'RGPD art. 17.3.b + Código de Comercio art. 30',
  },
  {
    what: 'Evidencias de consentimiento y de envíos',
    basis: 'LSSI art. 21 + criterio AEPD',
  },
  {
    what: 'Reseñas públicas en plataformas third-party',
    basis: 'Publicación voluntaria fuera de nuestro control',
  },
]

export function DsrFormPage() {
  const [type, setType] = useState<DsrType>('ACCESS')
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState<ApiResponse | null>(null)
  const [err, setErr] = useState<string | null>(null)
  const doneHeadingRef = useRef<HTMLHeadingElement | null>(null)

  // Accesibilidad / SEO: idioma y título de la página pública legal.
  useEffect(() => {
    document.title = 'Ejerce tus derechos RGPD — JR2'
    document.documentElement.lang = 'es'
  }, [])

  // Foco al heading de confirmación tras envío exitoso (anuncio AT).
  useEffect(() => {
    if (done && doneHeadingRef.current) {
      doneHeadingRef.current.focus()
    }
  }, [done])

  function reset() {
    setDone(null)
    setErr(null)
    setEmail('')
    setName('')
    setBody('')
  }

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    setBusy(true)
    setErr(null)
    try {
      const r = await publicApi<ApiResponse>('/p/rgpd/dsr', {
        method: 'POST',
        body: JSON.stringify({
          type,
          email,
          name: name || undefined,
          body,
        }),
      })
      setDone(r)
    } catch (e) {
      // Mensaje genérico al usuario público — sin trace técnica.
      console.error(e)
      setErr(
        'No hemos podido registrar tu solicitud. Comprueba el formulario o vuelve a intentarlo en unos minutos.',
      )
    } finally {
      setBusy(false)
    }
  }

  if (done) {
    return (
      <div className="min-h-screen bg-surface bp-grid flex items-center justify-center p-6">
        <div
          className="bp-card p-8 max-w-md w-full text-center"
          role="status"
          aria-live="polite"
        >
          <CheckCircle2 className="size-10 text-emerald-600 mx-auto mb-2" aria-hidden />
          <h1
            ref={doneHeadingRef}
            tabIndex={-1}
            className="text-lg font-medium outline-none"
          >
            Solicitud registrada correctamente
          </h1>
          <p className="text-sm text-ink-700/80 mt-2">{done.message}</p>
          <p className="text-xs font-mono text-ink-700/60 mt-3">
            ID de tu solicitud: {done.id}
          </p>
          <p className="text-[11px] text-ink-700/60 mt-2">
            Conserva este identificador. Si no recibes respuesta antes de la fecha
            indicada, puedes presentar reclamación ante la AEPD (
            <a
              href="https://www.aepd.es"
              className="text-blueprint-700 underline"
              target="_blank"
              rel="noreferrer"
            >
              www.aepd.es
            </a>
            ).
          </p>
          {done.retentionDisclosures && done.retentionDisclosures.length > 0 && (
            <div className="mt-4 text-left text-[11px] text-ink-700/70">
              <p className="font-medium mb-1">Datos que NO podremos suprimir (y por qué):</p>
              <ul className="list-disc pl-4 space-y-0.5">
                {done.retentionDisclosures.map((d, i) => (
                  <li key={i}>
                    <strong>{d.what}</strong> — {d.basis}
                  </li>
                ))}
              </ul>
            </div>
          )}
          <button
            type="button"
            onClick={reset}
            className="bp-btn mt-4 inline-flex items-center justify-center"
          >
            Enviar otra solicitud
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-surface bp-grid p-6">
      <div className="max-w-2xl mx-auto">
        <header className="mb-4">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blueprint-700">
            JR2 MKT · legal
          </div>
          <h1 className="text-2xl font-medium tracking-tight">Ejerce tus derechos RGPD</h1>
          <p className="text-sm text-ink-700/70 mt-2">
            Como titular de datos personales puedes ejercer los derechos reconocidos en los
            artículos 15-22 del Reglamento General de Protección de Datos (RGPD). Rellena este
            formulario y te responderemos en el plazo máximo de <strong>un mes</strong> (RGPD
            art. 12.3), prorrogable a dos meses adicionales en casos especialmente complejos
            previa comunicación motivada al titular dentro del primer mes.
          </p>
          <p className="text-[11px] text-ink-700/60 mt-2">
            Para evitar la suplantación de identidad podemos pedirte verificación adicional
            antes de procesar peticiones sensibles (acceso, supresión, portabilidad). Indica un
            email al que podamos contactarte que sea el mismo con el que interactuaste con
            nosotros.
          </p>
          <p className="text-[11px] text-ink-700/60 mt-2">
            Los campos marcados con <span className="text-red-600" aria-hidden>*</span>
            <span className="sr-only">asterisco</span> son obligatorios. Para evitar abuso
            limitamos el formulario a un máximo de 15 envíos por hora desde la misma IP.
          </p>
        </header>

        {type === 'ERASURE' && (
          <div className="bp-card p-3 mb-3 text-[11px] text-ink-700/80 border-l-4 border-blueprint-600">
            <p className="font-medium mb-1">Antes de pedir la supresión, debes saber:</p>
            <ul className="list-disc pl-4 space-y-0.5">
              {ERASURE_PREVIEW.map((d, i) => (
                <li key={i}>
                  <strong>{d.what}</strong> — {d.basis}
                </li>
              ))}
            </ul>
            <p className="mt-2 text-ink-700/60">
              Recibirás el listado completo en la confirmación. La supresión cubre todo lo
              demás (mensajes, consentimientos, etiquetas, preferencias).
            </p>
          </div>
        )}

        <form
          onSubmit={(e) => void submit(e)}
          className="bp-card p-6 space-y-3"
          noValidate
        >
          <label className="block" htmlFor="dsr-type">
            <span className="text-xs text-ink-700">
              Tipo de petición <span className="text-red-600" aria-hidden>*</span>
              <span className="sr-only">(obligatorio)</span>
            </span>
            <select
              id="dsr-type"
              value={type}
              onChange={(e) => setType(e.target.value as DsrType)}
              required
              aria-required="true"
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm bg-white"
            >
              {TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block" htmlFor="dsr-email">
            <span className="text-xs text-ink-700">
              Email de contacto <span className="text-red-600" aria-hidden>*</span>
              <span className="sr-only">(obligatorio)</span>
            </span>
            <input
              id="dsr-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              aria-required="true"
              autoComplete="email"
              inputMode="email"
              spellCheck={false}
              placeholder="el mismo email con el que nos contactaste"
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block" htmlFor="dsr-name">
            <span className="text-xs text-ink-700">Nombre (opcional)</span>
            <input
              id="dsr-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm"
            />
          </label>

          <label className="block" htmlFor="dsr-body">
            <span className="text-xs text-ink-700">
              Detalle de tu solicitud <span className="text-red-600" aria-hidden>*</span>
              <span className="sr-only">(obligatorio)</span>
            </span>
            <textarea
              id="dsr-body"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              required
              aria-required="true"
              autoComplete="off"
              rows={6}
              placeholder="Cuéntanos qué necesitas. Si solicitas rectificación, indica qué dato y cuál es el correcto."
              className="mt-1 w-full border border-ink-900/15 rounded px-2 py-1.5 text-sm min-h-[140px]"
            />
          </label>

          <p className="text-[11px] text-ink-700/60">
            Al enviar esta solicitud autorizas el tratamiento de tus datos (email, nombre,
            mensaje, IP de origen) exclusivamente para tramitar tu petición y mantener el
            registro de evidencias durante el plazo legalmente exigible. Responsable del
            tratamiento: JR2.
          </p>

          {err && (
            <div
              role="alert"
              className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1"
            >
              {err}
            </div>
          )}

          <button type="submit" className="bp-btn-primary w-full justify-center" disabled={busy}>
            {busy ? 'Enviando…' : 'Enviar petición'}
          </button>
        </form>

        <footer className="mt-4 text-[11px] text-ink-700/50 text-center">
          ¿Quieres ver qué terceros tratan tus datos por nuestra cuenta?{' '}
          <a href="/p/legal/sub-procesadores" className="text-blueprint-700 underline">
            Lista de sub-procesadores
          </a>
          .
        </footer>
      </div>
    </div>
  )
}
